import { supabase } from '../config/supabase'
import { applySalonFilter, preferLocationRows } from '../utils/salonScope'

/**
 * Fetch all services
 */
export const getServices = async () => {
  try {
    let query = supabase
      .from('services')
      .select('*')
      .eq('is_active', true)
    query = applySalonFilter(query, { includeBrandWide: true })
    const { data, error } = await query.order('name', { ascending: true })

    if (error) throw error
    return { data: preferLocationRows(data), error: null }
  } catch (error) {
    console.error('Error fetching services:', error)
    return { data: null, error }
  }
}

/**
 * Fetch services by category
 */
export const getServicesByCategory = async (categoryId) => {
  try {
    let query = supabase
      .from('services')
      .select('*')
      .eq('category_id', categoryId)
      .eq('is_active', true)
    query = applySalonFilter(query, { includeBrandWide: true })
    const { data, error } = await query.order('name', { ascending: true })

    if (error) throw error
    return { data: preferLocationRows(data), error: null }
  } catch (error) {
    console.error('Error fetching services by category:', error)
    return { data: null, error }
  }
}

/**
 * Fetch a single service by ID
 */
export const getServiceById = async (id) => {
  try {
    const { data, error } = await supabase
      .from('services')
      .select('*, service_categories(name)')
      .eq('id', id)
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error fetching service:', error)
    return { data: null, error }
  }
}

/**
 * Create a new service
 */
export const createService = async (serviceData) => {
  try {
    const { getSelectedSalonId } = await import('../utils/salonScope')
    const payload = {
      ...serviceData,
      salon_id: serviceData.salon_id || getSelectedSalonId() || null,
    }
    const { data, error } = await supabase
      .from('services')
      .insert([payload])
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error creating service:', error)
    return { data: null, error }
  }
}

/**
 * Update a service
 */
export const updateService = async (id, updates) => {
  try {
    const { data, error } = await supabase
      .from('services')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error updating service:', error)
    return { data: null, error }
  }
}

/**
 * Delete a service
 */
export const deleteService = async (id) => {
  try {
    const { error } = await supabase
      .from('services')
      .delete()
      .eq('id', id)

    if (error) throw error
    return { error: null }
  } catch (error) {
    console.error('Error deleting service:', error)
    return { error }
  }
}

/**
 * Soft delete (mark as inactive)
 */
export const deactivateService = async (id) => {
  try {
    const { data, error } = await supabase
      .from('services')
      .update({ 
        is_active: false,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error deactivating service:', error)
    return { data: null, error }
  }
}

/**
 * Search services by name
 */
export const searchServices = async (searchTerm) => {
  try {
    let query = supabase
      .from('services')
      .select('*, service_categories(name)')
      .eq('is_active', true)
    query = applySalonFilter(query, { includeBrandWide: true })
    const { data, error } = await query
      .ilike('name', `%${searchTerm}%`)
      .order('name', { ascending: true })

    if (error) throw error
    return { data: preferLocationRows(data), error: null }
  } catch (error) {
    console.error('Error searching services:', error)
    return { data: null, error }
  }
}
